<template>
  <v-select
    v-model="localValue"
    class="tms"
    :options="normalizedOptions"
    label="name"
    :reduce="reduceById"
    :get-option-label="getOptionLabel"
    :multiple="true"
    :close-on-select="false"
    :clearable="true"
    :searchable="true"
    :placeholder="placeholder"
  >
    <!-- SAFE option slot: supports both { option, selected } and spread props -->
    <template slot="option" slot-scope="slotProps">
      <div
        class="tms-opt"
        role="option"
        :aria-selected="isChecked(slotProps)"
        tabindex="0"
        @click.prevent.stop="onRowClick($event, slotProps)"
        @mousedown.prevent.stop
        @keydown.enter.prevent.stop="onRowKeydown($event, slotProps)"
        @keydown.space.prevent.stop="onRowKeydown($event, slotProps)"
      >
        <!--        <input
          type="checkbox"
          class="form-check-input me-2"
          :checked="isChecked(slotProps)"
          @click.stop="toggleChecked(slotProps)"
        />-->
        <span class="tms-label">{{ slotLabel(slotProps) }}</span>
      </div>
    </template>

    <template #no-options>Nieko nerasta…</template>
  </v-select>
</template>

<script>
import 'vue-select/dist/vue-select.css'
import vSelect from 'vue-select'

export default {
  name: 'TenderMultiSelect',
  components: { 'v-select': vSelect },

  // Vue 2 v-model (array of ID strings)
  model: { prop: 'value', event: 'input' },

  props: {
    value: { type: Array, default: () => [] }, // e.g., ["7n8Xvz8M", "03wrNREW"]
    options: { type: Array, default: () => [] }, // e.g., [{ id, name }] or { id, title } or primitives
    placeholder: { type: String, default: 'Paieška…' },
  },

  computed: {
    // Normalize every option to always have { id:string, name:string }
    normalizedOptions() {
      return (this.options || []).map((o) => {
        if (o && typeof o === 'object') {
          const id =
            o.id != null ? String(o.id) : o.value != null ? String(o.value) : ''
          const name =
            o.name != null && o.name !== ''
              ? String(o.name)
              : o.title != null && o.title !== ''
                ? String(o.title)
                : o.label != null && o.label !== ''
                  ? String(o.label)
                  : id
          return { ...o, id, name }
        }
        const id = String(o ?? '')
        return { id, name: id }
      })
    },

    // v-model proxy
    localValue: {
      get() {
        return Array.isArray(this.value) ? this.value.map((v) => String(v)) : []
      },
      set(next) {
        const out = (next || []).map((v) => String(v))
        this.$emit('input', out)
        this.$emit('change', out)
      },
    },

    // id -> name map (for labeling when only ID is available)
    idToName() {
      const m = new Map()
      for (const o of this.normalizedOptions) m.set(o.id, o.name)
      return m
    },
  },

  methods: {
    reduceById(opt) {
      // vue-select passes our normalized option object here
      if (opt && typeof opt === 'object') {
        if (opt.id != null) return String(opt.id)
        if (opt.value != null) return String(opt.value)
      }
      return String(opt ?? '')
    },

    // Used by vue-select to show labels for chips & input text (object OR ID)
    getOptionLabel(valOrObj) {
      if (valOrObj && typeof valOrObj === 'object') {
        return valOrObj.name != null && valOrObj.name !== ''
          ? String(valOrObj.name)
          : String(valOrObj.id ?? '')
      }
      const id = String(valOrObj ?? '')
      return this.idToName.get(id) || id
    },

    // SAFELY get label inside the option slot for ANY slot shape/version
    slotLabel(slotProps) {
      // Case A: { option, selected, ... }
      const o = slotProps && slotProps.option
      if (o && typeof o === 'object') return this.getOptionLabel(o)

      // Case B: option fields are spread into slot props (e.g., { id, name, ... })
      if (slotProps && typeof slotProps === 'object') {
        if (slotProps.name != null && slotProps.name !== '')
          return String(slotProps.name)
        if (slotProps.title != null && slotProps.title !== '')
          return String(slotProps.title)
        if (slotProps.label != null && slotProps.label !== '')
          return String(slotProps.label)
        if (slotProps.id != null) return String(slotProps.id)
      }

      // Last resort (shouldn’t hit)
      return ''
    },
    isChecked(slotProps) {
      // slot may be { option, ... } or spread fields → use whichever exists
      const raw = slotProps && slotProps.option ? slotProps.option : slotProps
      const id = String(this.reduceById(raw))
      return (this.localValue || []).includes(id)
    },
    toggleChecked(slotProps) {
      const raw = slotProps && slotProps.option ? slotProps.option : slotProps
      const id = String(this.reduceById(raw))
      const next = Array.isArray(this.localValue) ? this.localValue.slice() : []
      const idx = next.indexOf(id)
      if (idx >= 0) next.splice(idx, 1)
      else next.push(id)
      this.localValue = next // triggers your v-model update
    },

    onRowMouseDown(evt, slotProps) {
      // If the original click is on the checkbox, do nothing (checkbox handler will run).
      if (
        evt.target &&
        (evt.target.tagName === 'INPUT' || evt.target.closest('input'))
      )
        return
      this.toggleChecked(slotProps)
    },

    onRowClick(evt, slotProps) {
      // If the click originated on the checkbox, let its handler do the work.
      if (
        evt.target &&
        (evt.target.tagName === 'INPUT' || evt.target.closest('input'))
      )
        return
      this.toggleChecked(slotProps) // 🔁 toggles select/unselect on label click
    },

    onRowKeydown(evt, slotProps) {
      // Keyboard toggle when the row has focus (but not the checkbox)
      if (
        evt.target &&
        (evt.target.tagName === 'INPUT' || evt.target.closest('input'))
      )
        return
      this.toggleChecked(slotProps)
    },
  },
}
</script>

<style scoped lang="scss">
.tms {
  font-size: 14px;
}

.tms :deep(.vs__selected),
.tms :deep(.vs__search),
.tms :deep(.vs__placeholder) {
  font-size: 14px !important;
}

.tms :deep(.vs__selected) {
  line-height: 1 !important;
  padding: 4px 10px !important;
  border-radius: 999px !important;
  box-sizing: border-box;
}

.tms :deep(.vs__selected-options) {
  gap: 4px !important;
  align-items: center;
}

.tms :deep(.vs__dropdown-toggle) {
  min-height: 36px !important;
}

.tms :deep(.vs__dropdown-toggle) {
  border-radius: 12px;
  border: 1px solid #e6ebf2 !important;
  background: #fff;
  min-height: 36px;
  padding: 6px 4px 4px !important;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;
}
.tms :deep(.vs--open .vs__dropdown-toggle),
.tms :deep(.vs__dropdown-toggle:focus-within) {
  border-color: rgba(172, 172, 172, 0.65) !important;
  box-shadow: none !important;
}

/* Input area + chips spacing */
.tms :deep(.vs__selected-options) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-left: 3px;
}
.tms :deep(.vs__search) {
  padding: 4px 6px !important;
  margin: 2px 0 !important;
  min-width: 6ch;
}

/* ==== Chips (selected tags) ==== */
.tms :deep(.vs__selected) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 0.82rem;
  line-height: 1;
  border-radius: 999px;
  background: #f4f4f5;
  color: #18181b;
  border: 1px solid #e9e9ee;
  margin: 2px 2px 2px 0 !important;
}
.tms :deep(.vs__deselect) {
  opacity: 0.65;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.tms :deep(.vs__deselect:hover) {
  opacity: 1;
  transform: scale(1.08);
}

/* Clear & caret icons */
.tms :deep(.vs__actions) {
  gap: 6px;
  padding-right: 6px;
}
.tms :deep(.vs__clear) {
  opacity: 0.55;
}
.tms :deep(.vs__clear:hover) {
  opacity: 1;
}
.tms :deep(.vs__open-indicator) {
  transition: transform 0.2s ease;
  margin-left: 2px;
}
.tms :deep(.vs--open .vs__open-indicator) {
  transform: rotate(180deg);
}

/* Placeholder */
.tms :deep(.vs__placeholder) {
  color: #9aa3b2;
  padding-left: 2px;
}

/* Dropdown menu */
.tms :deep(.vs__dropdown-menu) {
  border: none;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.12),
    0 12px 28px rgba(0, 0, 0, 0.1);
  padding: 0 !important;
  margin-top: 2px;
  overflow: hidden;
}

/* Each option */
.tms :deep(.vs__dropdown-option) {
  padding: 6px 10px;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  color: #18181b;
  font-weight: 500;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

/* Hover / Focus */
.tms :deep(.vs__dropdown-option--highlight) {
  background: #f4f4f5;
  color: #18181b;
}

/* Selected option highlight */
.tms :deep(.vs__dropdown-option--selected) {
  background: #e0efff;
  color: #52525b;
  font-weight: 500;
}

.tms-opt {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background 0.15s ease;
}
.tms-opt:hover {
  background: transparent;
}

@media (max-width: 575.98px) {
  .tms :deep(.vs__dropdown-toggle) {
    min-height: 40px;
  }
  .tms :deep(.vs__selected) {
    font-size: 0.8rem;
    padding: 3px 8px;
  }
}
</style>
